/**
 * Bulk Document (contract) Upload tab.
 *
 * Upload ONE ZIP of PDFs → filename-only dry-run (matched / not-found /
 * duplicate / ambiguous / unparseable) → approval-token-gated filing to durable
 * storage (R2), one-by-one, read-after-write verified, versioning-on-conflict.
 *
 * The ZIP is chunk-uploaded (bypasses proxy body limits for 500+ files); the
 * server unzips and classifies by filename only, then files every MATCHED PDF.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Alert, AlertDescription } from '../../components/ui/alert';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Progress } from '../../components/ui/progress';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import {
  Upload, FileArchive, Loader2, Play, ShieldCheck, AlertTriangle,
  RefreshCw, CheckCircle, XCircle, Search, Download,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CHUNK_SIZE = 5 * 1024 * 1024; // 5 MB
const CHUNK_RETRIES = 5; // attempts per chunk before giving up
const CHUNK_TIMEOUT_MS = 120000; // per-attempt timeout

const RESULT_META = {
  matched: { cls: 'bg-green-100 text-green-700 dark:bg-green-900/30', label: 'MATCHED' },
  filed: { cls: 'bg-green-600 text-white', label: 'FILED' },
  not_found: { cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30', label: 'NOT FOUND' },
  duplicate: { cls: 'bg-brand-brown/15 text-brand-brown', label: 'DUPLICATE' },
  ambiguous: { cls: 'bg-brand-brown/15 text-brand-brown', label: 'AMBIGUOUS' },
  unparseable: { cls: 'bg-gray-100 text-gray-600 dark:bg-gray-800', label: 'UNPARSEABLE' },
  skip_non_pdf: { cls: 'bg-gray-100 text-gray-500 dark:bg-gray-800', label: 'NOT PDF' },
  skip_oversize: { cls: 'bg-red-100 text-red-700 dark:bg-red-900/30', label: 'OVERSIZE' },
  failed: { cls: 'bg-red-100 text-red-700 dark:bg-red-900/30', label: 'FAILED' },
};

const fmtKb = (v) => (v == null ? '—' : `${(v / 1024).toFixed(0)} KB`);

export default function DocumentUploadTab() {
  const { t } = useLanguage();
  const { token } = useAuth();

  const [docTypes, setDocTypes] = useState([]);
  const [docTypeId, setDocTypeId] = useState('');
  const [file, setFile] = useState(null);
  const [uploadPct, setUploadPct] = useState(0);
  const [dryRunning, setDryRunning] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [report, setReport] = useState(null);
  const [execResult, setExecResult] = useState(null);
  const [batches, setBatches] = useState([]);
  const [error, setError] = useState('');
  const [chunkInfo, setChunkInfo] = useState(null); // {current, total}
  const [execProgress, setExecProgress] = useState(null); // {done, total, filed, failed}
  const uploadRef = useRef({ key: null, uploadId: null, done: null }); // resume state

  const authHeader = { Authorization: `Bearer ${token}` };

  const loadDocTypes = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/documents/types?scope=employee`, { headers: authHeader });
      if (!res.ok) return;
      const data = await res.json();
      // All active employee types are selectable — expiry is optional for all.
      const list = (data.document_types || []).filter((x) => x.is_active);
      setDocTypes(list);
      const contract = list.find((x) => x.name === 'Employment Contract');
      setDocTypeId((prev) => prev || (contract ? contract.id : (list[0]?.id || '')));
    } catch (e) { /* noop */ }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadBatches = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/import/documents/batches?limit=50`, { headers: authHeader });
      if (res.ok) setBatches((await res.json()).batches || []);
    } catch (e) { /* noop */ }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadDocTypes(); loadBatches(); }, [loadDocTypes, loadBatches]);

  const reset = () => {
    setFile(null); setReport(null); setExecResult(null); setError(''); setUploadPct(0);
    setChunkInfo(null); setExecProgress(null);
    uploadRef.current = { key: null, uploadId: null, done: null };
  };

  const fileKey = (f) => `${f.name}|${f.size}|${f.lastModified}`;

  // One chunk: up to CHUNK_RETRIES attempts with exponential backoff.
  // On final failure the error says WHICH chunk and WHY.
  const sendChunk = async (uploadId, i, total, blob) => {
    let lastReason = '';
    for (let attempt = 1; attempt <= CHUNK_RETRIES; attempt += 1) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), CHUNK_TIMEOUT_MS);
      try {
        const fd = new FormData();
        fd.append('upload_id', uploadId);
        fd.append('index', String(i));
        fd.append('total', String(total));
        fd.append('chunk', blob, 'part');
        const r = await fetch(`${API_URL}/api/import/documents/upload/chunk`, {
          method: 'POST', headers: authHeader, body: fd, signal: controller.signal,
        });
        clearTimeout(timer);
        if (r.ok) {
          const data = await r.json();
          if (data.size != null && data.size !== blob.size) {
            lastReason = `server received ${data.size} bytes, expected ${blob.size} (corrupted in transit)`;
          } else {
            return;
          }
        } else if (r.status === 401 || r.status === 403) {
          throw Object.assign(new Error('Not authorized to upload — sign in again.'), { noRetry: true });
        } else {
          let msg = '';
          try { msg = (await r.json())?.detail?.error || ''; } catch (_) { /* noop */ }
          lastReason = r.status === 413
            ? 'rejected as too large by the server/proxy (HTTP 413 — body size limit)'
            : `server error HTTP ${r.status}${msg ? ` — ${msg}` : ''}`;
        }
      } catch (err) {
        clearTimeout(timer);
        if (err.noRetry) throw err;
        lastReason = err.name === 'AbortError'
          ? `timed out after ${CHUNK_TIMEOUT_MS / 1000}s`
          : `network error (${err.message || 'connection dropped'})`;
      }
      if (attempt < CHUNK_RETRIES) {
        await new Promise((res) => setTimeout(res, Math.min(8000, 1000 * 2 ** (attempt - 1))));
      }
    }
    throw new Error(
      `Chunk ${i + 1}/${total} failed after ${CHUNK_RETRIES} attempts: ${lastReason}. `
      + 'Your progress is saved — click "Run Dry-Run" again to resume from this chunk.',
    );
  };

  const chunkedUpload = async (zipFile) => {
    const key = fileKey(zipFile);
    const total = Math.max(1, Math.ceil(zipFile.size / CHUNK_SIZE));
    let uploadId = uploadRef.current.key === key ? uploadRef.current.uploadId : null;
    let done = null;
    if (uploadId) {
      // RESUME: ask the server which chunks it already has.
      try {
        const st = await fetch(`${API_URL}/api/import/documents/upload/${uploadId}/status`, { headers: authHeader });
        if (st.ok) done = new Set((await st.json()).received_indices || []);
        else uploadId = null;
      } catch (_) { uploadId = null; }
    }
    if (!uploadId) {
      const initRes = await fetch(`${API_URL}/api/import/documents/upload/init`, { method: 'POST', headers: authHeader });
      if (!initRes.ok) throw new Error('Could not start upload.');
      uploadId = (await initRes.json()).upload_id;
      done = new Set();
    }
    uploadRef.current = { key, uploadId, done };
    setUploadPct(Math.round((done.size / total) * 100));
    for (let i = 0; i < total; i += 1) {
      if (done.has(i)) continue;
      setChunkInfo({ current: i + 1, total });
      const blob = zipFile.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
      await sendChunk(uploadId, i, total, blob);
      done.add(i);
      setUploadPct(Math.round((done.size / total) * 100));
    }
    setChunkInfo(null);
    uploadRef.current = { key: null, uploadId: null, done: null };
    return uploadId;
  };

  const handleDryRun = async () => {
    if (!file) { setError(t('docSelectZip', 'Select a .zip file containing the PDF documents.')); return; }
    if (!docTypeId) { setError(t('docSelectType', 'Select a document type.')); return; }
    setDryRunning(true); setError(''); setExecResult(null); setReport(null); setUploadPct(0);
    try {
      const uploadId = await chunkedUpload(file);
      const res = await fetch(
        `${API_URL}/api/import/documents/dry-run?upload_id=${uploadId}&document_type_id=${docTypeId}`,
        { method: 'POST', headers: authHeader },
      );
      const data = await res.json();
      if (!res.ok) { setError(data?.detail?.error || t('docDryRunFailed', 'Dry-run failed.')); return; }
      setReport(data);
      loadBatches();
    } catch (e) {
      setError(e.message || t('docDryRunFailed', 'Dry-run failed.'));
    } finally {
      setDryRunning(false);
    }
  };

  // Filing runs in the BACKGROUND on the server (no proxy timeout for 500+
  // files) — poll the batch until it flips to executed/failed.
  const pollExecution = async (batchId) => {
    for (;;) {
      await new Promise((res) => setTimeout(res, 2000));
      let b = null;
      try {
        const res = await fetch(`${API_URL}/api/import/documents/batches/${batchId}`, { headers: authHeader });
        if (!res.ok) continue;
        b = await res.json();
      } catch (_) { continue; }
      if (b.status === 'executing') {
        if (b.progress) setExecProgress(b.progress);
        continue;
      }
      if (b.status === 'executed') return b;
      if (b.status === 'failed') throw new Error(b.execution_error || 'Filing failed on the server.');
    }
  };

  const handleExecute = async () => {
    if (!report?.approval_token) { setError(t('docApprovalRequired', 'Run a dry-run with at least one MATCHED file first.')); return; }
    setExecuting(true); setError(''); setExecProgress(null);
    try {
      const res = await fetch(
        `${API_URL}/api/import/documents/execute?batch_id=${report.batch_id}`,
        { method: 'POST', headers: { ...authHeader, 'X-Import-Approval-Token': report.approval_token } },
      );
      const data = await res.json();
      if (!res.ok) { setError(data?.detail?.error || t('docExecuteFailed', 'Filing failed.')); return; }
      if (data.status === 'executing') {
        setExecProgress({ done: 0, total: data.total_matched, filed: 0, failed: 0 });
        const b = await pollExecution(data.batch_id);
        setExecResult({ summary: b.execution_summary || {}, outcomes: b.outcomes || [] });
      } else {
        setExecResult(data);
      }
      setReport(null);
      loadBatches();
    } catch (e) {
      setError(e.message || t('docExecuteFailed', 'Filing failed.'));
    } finally {
      setExecuting(false);
      setExecProgress(null);
    }
  };

  const downloadDryRunCsv = () => {
    const rows = execResult?.outcomes || report?.outcomes || [];
    if (rows.length === 0) return;
    const headers = ['Result', 'File Name', 'Parsed ID', 'ID Type', 'Employee',
      'Employee Number', 'Size (bytes)', 'Version', 'Note'];
    const esc = (v) => {
      const str = v === null || v === undefined ? '' : String(v);
      return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };
    const lines = [headers.join(',')];
    rows.forEach((o) => {
      lines.push([
        (o.result || '').toUpperCase(), o.file_name, o.parsed_id, o.id_type,
        o.employee_name, o.employee_number, o.size, o.version, o.message,
      ].map(esc).join(','));
    });
    const blob = new Blob(['\ufeff' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const tag = (report?.file_sha256 || execResult?.batch_id || '').slice(0, 8);
    a.download = `document-dry-run-${tag}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const s = report?.summary;
  const stats = s ? [
    { k: 'matched', label: t('docMatched', 'Matched'), val: s.matched, cls: 'text-green-600' },
    { k: 'not_found', label: t('docNotFound', 'Not Found'), val: s.not_found, cls: 'text-amber-600' },
    { k: 'duplicate', label: t('docDuplicate', 'Duplicate'), val: s.duplicate, cls: 'text-brand-brown' },
    { k: 'ambiguous', label: t('docAmbiguous', 'Ambiguous'), val: s.ambiguous, cls: 'text-brand-brown' },
    { k: 'unparseable', label: t('docUnparseable', 'Unparseable'), val: s.unparseable, cls: 'text-gray-500' },
    { k: 'skipped', label: t('docSkipped', 'Skipped'), val: (s.skip_non_pdf || 0) + (s.skip_oversize || 0), cls: 'text-gray-400' },
  ] : [];

  const tableOutcomes = execResult?.outcomes || report?.outcomes || [];

  return (
    <div className="space-y-4" data-testid="document-upload-tab">
      {error && (
        <Alert variant="destructive" data-testid="doc-error">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileArchive className="h-5 w-5 text-brand-orange" />
            {t('bulkDocUpload', 'Bulk Document Upload')}
          </CardTitle>
          <CardDescription>
            {t('bulkDocUploadHint',
              'Upload ONE ZIP of PDFs. Each file is matched to an employee by the 10-digit National ID (starts with 1) or Iqama (starts with 2) in its filename. A filename-only dry-run runs first; nothing is filed until you approve it. Files are stored permanently and verified; a conflicting document is kept as a new version.')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-lg border border-brand-orange/30 bg-brand-orange/5 p-3 text-sm" data-testid="doc-reason-note">
            <ShieldCheck className="inline h-4 w-4 mr-1 text-brand-orange" />
            {t('docRules', 'PDF only, ≤ 10 MB each. Stored permanently in durable storage (no expiry, no auto-delete), read-after-write verified before recording.')}
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">
                {t('docType', 'Document type')}
              </label>
              <Select value={docTypeId} onValueChange={setDocTypeId}>
                <SelectTrigger data-testid="doc-type-select">
                  <SelectValue placeholder={t('docSelectType', 'Select a document type')} />
                </SelectTrigger>
                <SelectContent>
                  {docTypes.map((dt) => (
                    <SelectItem key={dt.id} value={dt.id} data-testid={`doc-type-option-${dt.id}`}>
                      {dt.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">
                {t('docZipFile', 'ZIP of PDFs')}
              </label>
              <input
                type="file"
                accept=".zip,application/zip"
                data-testid="doc-zip-input"
                onChange={(e) => { setFile(e.target.files?.[0] || null); setReport(null); setExecResult(null); setError(''); setUploadPct(0); }}
                className="block w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-brand-black file:px-4 file:py-2 file:text-white hover:file:bg-brand-black/80"
              />
            </div>
          </div>

          {dryRunning && chunkInfo && (
            <div data-testid="doc-upload-progress">
              <div className="flex justify-between text-xs text-muted-foreground mb-1">
                <span>
                  {t('docUploading', 'Uploading ZIP…')}{' '}
                  ({t('docChunk', 'chunk')} {chunkInfo.current}/{chunkInfo.total})
                </span>
                <span>{uploadPct}%</span>
              </div>
              <Progress value={uploadPct} />
            </div>
          )}

          <div className="flex gap-2">
            <Button onClick={handleDryRun} disabled={!file || !docTypeId || dryRunning} data-testid="doc-dry-run-btn">
              {dryRunning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
              {t('docRunDryRun', 'Run Dry-Run')}
            </Button>
            {(report || execResult) && (
              <Button variant="outline" onClick={reset} data-testid="doc-reset-btn">
                <RefreshCw className="h-4 w-4 mr-2" />
                {t('reset', 'Reset')}
              </Button>
            )}
          </div>

          {execResult && (
            <Alert data-testid="doc-execution-result" className="border-green-300 bg-green-50 dark:bg-green-900/10">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription>
                {t('docFiled', 'Filed')}: <b>{execResult.summary.filed}</b>
                {execResult.summary.failed ? <>, {t('docFailed', 'Failed')}: <b className="text-red-600">{execResult.summary.failed}</b></> : null}
                . {t('docFiledNote', 'All filed PDFs are verified and stored permanently.')}{' '}
                {t('docReportEmailed', 'A copy of the filing report (CSV) has been emailed to you.')}
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {(report || execResult) && (
        <Card data-testid="doc-dry-run-report">
          <CardHeader>
            <div className="flex items-start justify-between gap-3">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5 text-brand-orange" />
                  {execResult ? t('docFilingResult', 'Filing Result') : t('docDryRunReport', 'Dry-Run Preview (nothing filed yet)')}
                </CardTitle>
                {report && (
                  <CardDescription className="font-mono text-xs break-all mt-1">
                    SHA-256: {report.file_sha256}
                  </CardDescription>
                )}
              </div>
              {(report?.outcomes?.length || execResult?.outcomes?.length) ? (
                <Button variant="outline" size="sm" onClick={downloadDryRunCsv} data-testid="doc-download-csv-btn">
                  <Download className="h-4 w-4 mr-2" />
                  {t('docDownloadCsv', 'Download report (CSV)')}
                </Button>
              ) : null}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {report && (
              <>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                  {stats.map((st) => (
                    <div key={st.k} className="p-3 rounded-lg border text-center" data-testid={`doc-stat-${st.k}`}>
                      <div className={`text-2xl font-bold ${st.cls}`}>{st.val}</div>
                      <div className="text-xs text-muted-foreground">{st.label}</div>
                    </div>
                  ))}
                </div>

                <div
                  className={`rounded-lg p-3 text-sm flex items-center gap-2 ${report.can_execute ? 'bg-green-50 text-green-700 dark:bg-green-900/10' : 'bg-amber-50 text-amber-700 dark:bg-amber-900/10'}`}
                  data-testid="doc-readiness"
                >
                  {report.can_execute
                    ? <><CheckCircle className="h-4 w-4" /> {t('docReady', 'Ready to file — an approval token is bound to this ZIP. Only MATCHED files will be filed.')}</>
                    : <><XCircle className="h-4 w-4" /> {t('docNotReady', 'No files matched an employee. Filing is not enabled.')}</>}
                </div>
              </>
            )}

            <div className="border rounded-lg overflow-hidden max-h-[520px] overflow-y-auto">
              <Table data-testid="doc-rows-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('result', 'Result')}</TableHead>
                    <TableHead>{t('file', 'File')}</TableHead>
                    <TableHead>{t('parsedId', 'ID')}</TableHead>
                    <TableHead>{t('employee', 'Employee')}</TableHead>
                    <TableHead className="text-right">{t('size', 'Size')}</TableHead>
                    <TableHead>{t('note', 'Note')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tableOutcomes.map((o, i) => {
                    const meta = RESULT_META[o.result] || { cls: '', label: o.result };
                    return (
                      <TableRow key={`${o.file_name}-${i}`} data-testid={`doc-row-${i}`}>
                        <TableCell><Badge className={meta.cls} data-testid={`doc-row-result-${i}`}>{meta.label}</Badge></TableCell>
                        <TableCell className="text-sm max-w-[220px] truncate" title={o.file_name}>
                          {o.file_name}
                          {o.version ? <span className="text-muted-foreground"> (v{o.version})</span> : ''}
                        </TableCell>
                        <TableCell className="text-xs font-mono">{o.parsed_id || '—'}</TableCell>
                        <TableCell className="text-sm">
                          {o.employee_name || '—'}
                          {o.employee_number ? <span className="text-muted-foreground"> ({o.employee_number})</span> : ''}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-xs">{fmtKb(o.size)}</TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-[240px]"><span className="line-clamp-2">{o.message}</span></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {report && (
              <div className="space-y-2">
                {executing && execProgress && (
                  <div data-testid="doc-exec-progress">
                    <div className="flex justify-between text-xs text-muted-foreground mb-1">
                      <span>{t('docFiling', 'Filing documents to durable storage…')}</span>
                      <span>{execProgress.done}/{execProgress.total}</span>
                    </div>
                    <Progress value={execProgress.total ? Math.round((execProgress.done / execProgress.total) * 100) : 0} />
                  </div>
                )}
                <div className="flex justify-end">
                  <Button
                    onClick={handleExecute}
                    disabled={!report.can_execute || executing}
                    className="bg-brand-black hover:bg-brand-black/80"
                    data-testid="doc-execute-btn"
                  >
                    {executing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
                    {t('docExecute', 'File Matched Documents')}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* History */}
      <Card data-testid="doc-batches-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg">{t('docUploadHistory', 'Document Upload History')}</CardTitle>
            <CardDescription>{t('docUploadHistoryHint', 'Who uploaded which ZIP (by SHA-256), when, status, and how many files were filed.')}</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={loadBatches} data-testid="doc-refresh-batches-btn">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          {batches.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground" data-testid="doc-batches-empty">
              {t('noDocBatches', 'No document uploads yet. A dry-run records a batch here.')}
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table data-testid="doc-batches-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('file', 'File')}</TableHead>
                    <TableHead>{t('docType', 'Type')}</TableHead>
                    <TableHead>{t('status', 'Status')}</TableHead>
                    <TableHead>{t('uploadedBy', 'By')}</TableHead>
                    <TableHead className="text-center">{t('files', 'Files')}</TableHead>
                    <TableHead className="text-center">{t('docFiled', 'Filed')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {batches.map((b) => (
                    <TableRow key={b.id} data-testid={`doc-batch-row-${b.id}`}>
                      <TableCell>
                        <div className="text-sm font-medium">{b.file_name}</div>
                        <div className="text-[10px] font-mono text-muted-foreground break-all">{(b.file_hash || '').slice(0, 16)}…</div>
                      </TableCell>
                      <TableCell className="text-xs">{b.document_type_name || '—'}</TableCell>
                      <TableCell>
                        {b.status === 'executed'
                          ? <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30">{t('executed', 'Executed')}</Badge>
                          : b.status === 'executing'
                            ? <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30">{t('executing', 'Executing…')}</Badge>
                            : b.status === 'failed'
                              ? <Badge className="bg-red-100 text-red-700 dark:bg-red-900/30">{t('failed', 'Failed')}</Badge>
                              : <Badge variant="outline">{t('pending', 'Pending')}</Badge>}
                      </TableCell>
                      <TableCell className="text-xs">{b.uploaded_by_email || b.uploaded_by}</TableCell>
                      <TableCell className="text-center">{b.total_files}</TableCell>
                      <TableCell className="text-center">{b.execution_summary?.filed ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
